// ---------------------------------------------- modules import
import M from "materialize-css";
import React, {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  useEffect
} from "react";
import { Link, useParams } from "react-router-dom";
import Spinner from "react-spinkit";

import { IUpdateFormProps } from "./common";
import { ThemeContext } from "../../../contexts/themeContext";
import { useAuth, usePostForUpdateForm } from "./hooks";
import PermissionHelper from "../../../store/helpers/permissionHelper";
import TagHelper from "../../../store/helpers/tagHelper";
import { pattern } from "../../../store/helpers/validationHelper";

// ---------------------------------------------- the component
const UpdateForm: FunctionComponent<IUpdateFormProps> = ({ menuIndex }) => {
  // ---------------------------------------------- router state
  const { post_id } = useParams();

  // ---------------------------------------------- local state
  const { auth, profile } = useAuth();
  const {
    post,
    isReady,
    isPostValid,
    categories,
    onChange,
    onUpdate
  } = usePostForUpdateForm(menuIndex, profile, post_id);

  const tags = TagHelper.toTags(post.tags);

  // ---------------------------------------------- handlers
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange(e.currentTarget.name, e.currentTarget.value);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    onUpdate(auth, profile, `/${PermissionHelper.getUrl(menuIndex)}`);
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    const selects = document.querySelectorAll("select");

    M.FormSelect.init(selects);
  }, []);

  useEffect(() => {
    const modals = document.querySelectorAll(".modal");

    M.Modal.init(modals);
  }, []);

  // ---------------------------------------------- content
  return (
    <ThemeContext.Consumer>
      {({ theme }) => (
        <div className="container row">
          <form className="card col s12 m10 offset-m1" onSubmit={handleSubmit}>
            <div
              className="card-content row"
              style={{ marginBottom: "0px", paddingBottom: "0px" }}
            >
              <span style={{ fontSize: "0.75rem" }}>
                {PermissionHelper.getName(menuIndex).toLowerCase()}
              </span>

              <span className="card-title" style={{ marginBottom: "20px" }}>
                UPDATE FORM
              </span>

              <div className="input-field col s12 m6">
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="validate"
                  pattern={pattern.post.title.toString().slice(1, -1)}
                  minLength={2}
                  maxLength={50}
                  value={post.title}
                  onChange={handleChange}
                  required
                />

                <label className="active" htmlFor="title">
                  Title
                </label>
              </div>

              <div className="input-field col s12 m6">
                <select
                  name="category"
                  defaultValue={post.category}
                  onChange={handleChange}
                >
                  <option value={0} disabled>
                    set category
                  </option>

                  {categories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>

                <label>Category</label>
              </div>

              <div className="input-field col s12" style={{ padding: "0px" }}>
                <div className="row" style={{ margin: "0px", padding: "0px" }}>
                  {tags.map(tag => (
                    <div
                      key={tag.index}
                      className="col s6 row"
                      style={{ margin: "0px", padding: "0px" }}
                    >
                      <div className="col s3">
                        <label>
                          <input
                            type="checkbox"
                            name="tags"
                            className="filled-in"
                            value={tag.index}
                            checked={tag.tagged}
                            onChange={handleChange}
                          />

                          <span />
                        </label>
                      </div>

                      <div
                        className="col s9"
                        style={{ fontSize: "0.65rem" }}
                      >{`#${tag.shortName.toLowerCase()}`}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="input-field card col s12 z-depth-3">
                <div
                  className="card-content row"
                  style={{ marginBottom: 0, paddingBottom: 0 }}
                >
                  <div id="codex-editor"></div>
                </div>

                {isReady ? null : (
                  <div className="center-align" style={{ marginTop: "3rem" }}>
                    <Spinner name="three-bounce" fadeIn="none" />
                  </div>
                )}

                <div
                  className="card-action right-align"
                  style={{ marginBottom: "0" }}
                />
              </div>
            </div>

            <div className="card-action row" style={{ marginBottom: "0" }}>
              <div className="col s6">
                <Link
                  to={`/${PermissionHelper.getUrl(menuIndex)}`}
                  className="btn-floating btn-small waves-effect waves-light"
                  style={{ backgroundColor: theme.ui }}
                >
                  <i className="material-icons left">arrow_back</i>
                </Link>
              </div>

              <div className="col s6 right-align">
                <button
                  type="button"
                  className={`btn-small waves-effect waves-light modal-trigger ${
                    isPostValid() ? "" : "disabled"
                  }`}
                  style={{ backgroundColor: theme.ui }}
                  data-target="alert"
                >
                  Submit<i className="material-icons right">send</i>
                </button>

                <div className="modal" id="alert">
                  <div className="modal-content center">
                    <h5>Confirm Update</h5>

                    <p>Are you sure you want to update this post?</p>
                  </div>

                  <div className="modal-footer">
                    <div className="container">
                      <button
                        type="submit"
                        className="btn modal-close"
                        style={{ backgroundColor: theme.ui }}
                      >
                        Yes, I'm sure
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </ThemeContext.Consumer>
  );
};

export default UpdateForm;
